import React from "react"
import { useEffect, useState, useRef } from "react"
import { api } from "../api/client"
import { useTheme } from "../context/ThemeContext"
import { useLanguage } from "../context/LanguageContext"
import { SkeletonCard, SkeletonText, SkeletonButton } from "../components/LoadingSpinner"
import ParticleBackground from "../components/ParticleBackground"
import WritingScene from "../components/WritingScene"
import ExerciseVocabulary from "../components/ExerciseVocabulary"
import { scrollToTop, scrollResultIntoView } from "../utils/scroll"

function WritingFeedbackPanel({ feedback, scoringSource, t }) {
  if (!feedback) return null
  const rubric = feedback.rubric
  return (
    <div className="writing-panel-3d mb-6 rounded-2xl border border-sky-500/25 bg-sky-500/5 p-4 sm:p-6 w-full min-w-0 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base sm:text-lg font-semibold text-foreground">{t("teacherFeedback")}</h3>
        <span className="text-xs text-muted-foreground">
          {scoringSource === "ai" ? t("gradedByAi") : t("gradedAutomatically")}
        </span>
      </div>
      {feedback.overall_comment && (
        <p className="text-foreground leading-relaxed text-sm sm:text-base">{feedback.overall_comment}</p>
      )}
      {rubric && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
          {[
            ["task_completion", t("rubricTask")],
            ["grammar", t("rubricGrammar")],
            ["vocabulary", t("rubricVocabulary")],
            ["organization", t("rubricOrganization")],
          ].map(([key, label]) =>
            rubric[key] != null ? (
              <div
                key={key}
                className="rounded-lg border border-border/60 bg-background/60 py-2 px-1"
              >
                <div className="font-semibold text-foreground tabular-nums">{rubric[key]}%</div>
                <div className="text-muted-foreground">{label}</div>
              </div>
            ) : null
          )}
        </div>
      )}
      {feedback.strengths?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 mb-2">
            {t("strengths")}
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
            {feedback.strengths.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}
      {feedback.improvements?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2">
            {t("howToImprove")}
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
            {feedback.improvements.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}
      {feedback.grammar_tips?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-sky-700 dark:text-sky-400 mb-2">
            {t("grammarTips")}
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
            {feedback.grammar_tips.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function translateDifficulty(level, t) {
  switch (level) {
    case "Beginner":
      return t("beginner")
    case "Intermediate":
      return t("intermediate")
    case "Advanced":
      return t("advanced")
    default:
      return level
  }
}

export default function Writing() {
  const { isDark } = useTheme()
  const { t } = useLanguage()
  const [view, setView] = useState("picker") // picker | practice
  const [lessons, setLessons] = useState([])
  const [lessonsLoading, setLessonsLoading] = useState(true)
  const [prompt, setPrompt] = useState(null)
  const [text, setText] = useState("")
  const [score, setScore] = useState(null)
  const [scoringSource, setScoringSource] = useState(null)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [difficulty, setDifficulty] = useState("all")
  const [wordCount, setWordCount] = useState(0)
  const [sentenceCount, setSentenceCount] = useState(0)
  const [attempts, setAttempts] = useState([])
  const [showResubmitModal, setShowResubmitModal] = useState(false)
  const [resubmitInfo, setResubmitInfo] = useState({ attempts: 0, bestScore: 0 })
  const [aiFeedback, setAiFeedback] = useState(null)
  const [submitNotice, setSubmitNotice] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [selectedText, setSelectedText] = useState("")
  const shapesRef = useRef(null)
  const sessionStartedAtRef = useRef(Date.now())
  const lessonsLoadedRef = useRef(false)

  const loadLessons = async (selectedDifficulty = difficulty) => {
    if (!lessonsLoadedRef.current) setLessonsLoading(true)
    setError("")
    try {
      const data = await api.listWritingPrompts(
        selectedDifficulty !== "all" ? selectedDifficulty : null
      )
      setLessons(data.exercises || [])
      lessonsLoadedRef.current = true
    } catch (e) {
      setError(e.message)
      setLessons([])
    } finally {
      setLessonsLoading(false)
    }
  }

  const loadPrompt = async ({ title, selectedDifficulty = difficulty } = {}) => {
    scrollToTop()
    setLoading(true)
    setError("")
    setScore(null)
    setScoringSource(null)
    setText("")
    setWordCount(0)
    setSentenceCount(0)
    setAiFeedback(null)
    setSubmitNotice("")
    setSubmitted(false)
    sessionStartedAtRef.current = Date.now()

    try {
      const p = await api.writingPrompt({
        difficulty: selectedDifficulty !== "all" ? selectedDifficulty : null,
        title: title || null,
      })
      setPrompt(p)
      setView("practice")
      setAttempts([])
      scrollToTop()

      if (p.lesson_id) {
        try {
          const history = await api.getWritingAttempts(p.lesson_id)
          setAttempts(history.attempts || [])
        } catch {
          /* no prior attempts */
        }
      }
    } catch (e) {
      setError(e.message)
      setView("picker")
    } finally {
      setLoading(false)
    }
  }

  const backToLessons = () => {
    setView("picker")
    setPrompt(null)
    setScore(null)
    setText("")
    setAttempts([])
    setAiFeedback(null)
    setSubmitNotice("")
    setSubmitted(false)
    setError("")
    scrollToTop()
  }

  useEffect(() => {
    const words = text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0)
    const sentences = text.split(/[.!?]+/).filter((sentence) => sentence.trim().length > 0)
    setWordCount(words.length)
    setSentenceCount(sentences.length)
  }, [text])

  useEffect(() => {
    loadLessons()
  }, [])

  useEffect(() => {
    const mq = window.matchMedia("(pointer: fine)")
    const onMove = (e) => {
      if (!mq.matches) return
      const layer = shapesRef.current
      if (!layer) return
      const x = (e.clientX / window.innerWidth - 0.5) * 2
      const y = (e.clientY / window.innerHeight - 0.5) * 2
      layer.style.transform = `rotateX(${y * -3}deg) rotateY(${x * 5}deg)`
    }
    window.addEventListener("mousemove", onMove)
    return () => window.removeEventListener("mousemove", onMove)
  }, [])

  const getDifficultyColor = (level) => {
    switch (level) {
      case "Beginner":
        return "from-amber-500 to-orange-600"
      case "Intermediate":
        return "from-orange-500 to-amber-700"
      case "Advanced":
        return "from-primary to-accent"
      default:
        return "from-muted-foreground/70 to-muted-foreground"
    }
  }

  const submit = async (force = false) => {
    if (!prompt || !text.trim()) return

    if (attempts.length > 0 && !force) {
      const bestScore = Math.max(...attempts.map((a) => a.score ?? 0))
      setResubmitInfo({
        attempts: attempts.length,
        bestScore: Number.isFinite(bestScore) ? bestScore : 0,
      })
      setShowResubmitModal(true)
      return
    }

    setShowResubmitModal(false)
    setSubmitting(true)
    setAiFeedback(null)
    setScore(null)
    setSubmitNotice("")
    setSubmitted(false)
    setError("")
    try {
      const res = await api.submitWriting({
        lesson_id: prompt.lesson_id,
        text,
        time_spent: Math.max(
          1,
          Math.round((Date.now() - sessionStartedAtRef.current) / 1000)
        ),
        prompt_title: prompt.title,
        task_prompt: prompt.prompt,
        difficulty: prompt.difficulty || "Beginner",
        min_words: prompt.min_words || 50,
        example: prompt.example || null,
      })
      setSubmitted(Boolean(res.submitted))
      setScore(res.score)
      scrollResultIntoView()
      setScoringSource(res.scoring_source || "length")
      setAiFeedback(res.feedback || null)
      setSubmitNotice(res.notice || "")

      if (prompt.lesson_id) {
        const history = await api.getWritingAttempts(prompt.lesson_id)
        setAttempts(history.attempts || [])
      }
    } catch (e) {
      setError(e.message)
      setSubmitted(false)
    } finally {
      setSubmitting(false)
    }
  }

  const levelOptions = [
    { key: "all", label: t("allLevels"), color: "from-muted-foreground/70 to-muted-foreground" },
    { key: "Beginner", label: t("beginner"), color: "from-amber-500 to-orange-600" },
    { key: "Intermediate", label: t("intermediate"), color: "from-orange-500 to-amber-700" },
    { key: "Advanced", label: t("advanced"), color: "from-primary to-accent" },
  ]

  const minWordsHint = t("minWordsSuggested").replace(
    "{count}",
    String(prompt?.min_words || 50)
  )

  if (lessonsLoading || (view === "practice" && loading)) {
    return (
      <div className="min-h-screen bg-background relative overflow-hidden">
        <ParticleBackground particleCount={22} className="z-0 opacity-12 dark:opacity-8" />
        <WritingScene />
        <div className="relative z-10 p-4 sm:p-6 max-w-4xl mx-auto w-full min-w-0">
          <div className="animate-pulse space-y-8">
            <div className="text-center space-y-4">
              <SkeletonCard className="w-20 h-20 mx-auto rounded-2xl" />
              <SkeletonText lines={2} className="max-w-md mx-auto" />
            </div>
            <div className="flex justify-center gap-3 sm:gap-4 flex-wrap">
              <SkeletonButton className="w-24" />
              <SkeletonButton className="w-24" />
              <SkeletonButton className="w-24" />
            </div>
            <div className="flex flex-col gap-3">
              <SkeletonCard className="h-44 rounded-2xl" />
              <SkeletonCard className="h-44 rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error && view === "picker" && lessons.length === 0) {
    return (
      <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center p-4">
        <ParticleBackground particleCount={22} className="z-0 opacity-12 dark:opacity-8" />
        <div className="relative z-10 bg-card/90 backdrop-blur-md rounded-2xl border border-border p-6 sm:p-8 max-w-md w-full text-center writing-panel-3d">
          <h3 className="text-lg font-semibold text-foreground mb-2">{t("somethingWentWrong")}</h3>
          <p className="text-destructive mb-6 text-sm break-words">{error}</p>
          <button
            type="button"
            onClick={() => loadLessons()}
            className="button-glow bg-gradient-to-r from-primary to-accent text-primary-foreground px-6 py-2.5 rounded-xl font-medium"
          >
            {t("tryAgain")}
          </button>
        </div>
      </div>
    )
  }

  if (view === "picker") {
    return (
      <div className="min-h-screen bg-background relative overflow-hidden">
        <ParticleBackground particleCount={22} className="z-0 opacity-12 dark:opacity-8" />
        <WritingScene layerRef={shapesRef} />
        <div
          className="fixed inset-0 pointer-events-none z-[1] opacity-55"
          style={{
            background: isDark
              ? "radial-gradient(ellipse 50% 40% at 70% 20%, rgb(56 189 248 / 0.1), transparent 55%), radial-gradient(ellipse 40% 35% at 20% 75%, rgb(100 116 139 / 0.1), transparent)"
              : "radial-gradient(ellipse 50% 40% at 70% 20%, rgb(56 189 248 / 0.1), transparent 55%), radial-gradient(ellipse 40% 35% at 20% 75%, rgb(100 116 139 / 0.08), transparent)",
          }}
          aria-hidden
        />

        <div key="picker" className="relative z-10 writing-scene view-stage">
          <div className="writing-picker max-w-3xl mx-auto px-3 sm:px-4 py-6 md:py-10 w-full min-w-0">
            <header className="writing-hero-minimal writing-picker-hero mb-6 md:mb-8 text-center">
              <h1 className="writing-hero-title">{t("writing")}</h1>
              <div className="writing-hero-accent writing-picker-accent" aria-hidden />
              <p className="writing-picker-lead">{t("writingPickShelf")}</p>
            </header>

            <section className="writing-desk-filters mb-6 sm:mb-8">
              <p className="writing-desk-filters-label">{t("writingChooseLevel")}</p>
              <div className="writing-desk-keys">
                {levelOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      setDifficulty(opt.key)
                      loadLessons(opt.key)
                    }}
                    className={`writing-desk-key ${
                      difficulty === opt.key ? "writing-desk-key--active" : ""
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </section>

            <section className="w-full min-w-0">
              <div className="writing-prompt-header">
                <h2 className="writing-prompt-header-title">{t("writingChooseLesson")}</h2>
                <p className="writing-prompt-header-count">
                  {lessons.length}{" "}
                  {lessons.length === 1 ? t("writingLesson") : t("writingLessons")}
                </p>
              </div>

              {error && <p className="text-sm text-destructive mb-4">{error}</p>}

              <div className="writing-prompt-stack">
                {lessons.map((lesson) => (
                  <button
                    key={lesson.id || lesson.title}
                    type="button"
                    onClick={() =>
                      loadPrompt({
                        title: lesson.title,
                        selectedDifficulty: difficulty,
                      })
                    }
                    className="writing-prompt-sheet writing-panel-3d group"
                  >
                    <div className="writing-prompt-sheet-pin" aria-hidden />
                    <div className="writing-prompt-sheet-pencil" aria-hidden>
                      <span className="writing-prompt-pencil-body" />
                      <span className="writing-prompt-pencil-tip" />
                    </div>

                    <div className="writing-prompt-sheet-head">
                      <h3 className="writing-prompt-sheet-title">{lesson.title}</h3>
                      <span
                        className={`writing-prompt-difficulty writing-prompt-difficulty--${(lesson.difficulty || "default").toLowerCase()}`}
                      >
                        {translateDifficulty(lesson.difficulty, t)}
                      </span>
                    </div>

                    <p className="writing-prompt-sheet-hint">
                      {t("minWordsSuggested").replace("{count}", String(lesson.min_words || 50))}
                      {lesson.vocabulary_count > 0
                        ? ` · ${lesson.vocabulary_count} ${t("speakingVocabWords")}`
                        : ""}
                    </p>

                    <div className="writing-prompt-ruled" aria-hidden>
                      <span />
                      <span />
                      <span />
                      <span />
                    </div>

                    <p className="writing-prompt-excerpt">{lesson.prompt}</p>

                    <span className="writing-prompt-cta">
                      {t("writingStartLesson")}
                      <span aria-hidden>→</span>
                    </span>
                  </button>
                ))}
              </div>

              {lessons.length === 0 && !error && (
                <div className="writing-panel-3d writing-prompt-empty">
                  {t("writingNoLessons")}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    )
  }

  if (!prompt) return null

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <ParticleBackground particleCount={22} className="z-0 opacity-12 dark:opacity-8" />
      <WritingScene layerRef={shapesRef} />

      <div
        className="fixed inset-0 pointer-events-none z-[1] opacity-55"
        style={{
          background: isDark
            ? "radial-gradient(ellipse 50% 40% at 70% 20%, rgb(var(--primary-rgb) / 0.1), transparent 55%), radial-gradient(ellipse 40% 35% at 20% 75%, rgb(var(--accent-rgb) / 0.08), transparent)"
            : "radial-gradient(ellipse 50% 40% at 70% 20%, rgb(var(--primary-rgb) / 0.1), transparent 55%), radial-gradient(ellipse 40% 35% at 20% 75%, rgb(var(--accent-rgb) / 0.07), transparent)",
        }}
        aria-hidden
      />

      <div key="practice" className="relative z-10 writing-scene view-stage">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-6 md:py-10 w-full min-w-0">
          <header className="writing-hero-minimal mb-6 md:mb-8">
            <h1 className="writing-hero-title">{t("writing")}</h1>
            <div className="writing-hero-accent" aria-hidden />
          </header>

          <div className="mb-4 sm:mb-6">
            <button
              type="button"
              onClick={backToLessons}
              className="text-sm font-medium text-primary hover:underline"
            >
              ← {t("writingBackToLessons")}
            </button>
          </div>

          <div className="writing-panel-3d rounded-2xl border border-border bg-card/80 backdrop-blur-sm p-4 sm:p-6 mb-6 w-full min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <h2 className="text-lg sm:text-xl font-bold text-foreground break-words">{prompt.title}</h2>
              <span
                className={`text-xs font-semibold px-3 py-1 rounded-full bg-gradient-to-r ${getDifficultyColor(
                  prompt.difficulty
                )} text-white shrink-0`}
              >
                {translateDifficulty(prompt.difficulty, t)}
              </span>
            </div>
            <p
              className="text-foreground/90 leading-relaxed mb-4 text-sm sm:text-base select-text"
              onMouseUp={() => {
                const picked = window.getSelection()?.toString().trim()
                if (picked) setSelectedText(picked)
              }}
            >
              {prompt.prompt}
            </p>
            <ExerciseVocabulary
              skill="writing"
              lessonId={prompt.lesson_id}
              lessonTitle={prompt.title}
              sourceRef={prompt.title}
              selectedText={selectedText}
              onClearSelection={() => setSelectedText("")}
              contextText={`${prompt.prompt} ${prompt.example || ""} ${text}`}
              suggestions={prompt.vocabulary || []}
            />
            {prompt.example && (
              <details className="text-sm">
                <summary className="cursor-pointer text-primary font-medium">
                  {t("showExampleAnswer")}
                </summary>
                <p className="mt-2 text-muted-foreground italic border-l-2 border-primary/30 pl-3">
                  {prompt.example}
                </p>
              </details>
            )}
          </div>

          {attempts.length > 0 && (
            <div className="mb-4 text-sm text-muted-foreground">
              {t("previousAttempts")}: {attempts.length}
              <span className="ml-2">
                · {t("bestScoreLabel")}: {Math.max(...attempts.map((a) => a.score))}%
              </span>
            </div>
          )}

          <section className="mb-6 w-full min-w-0">
            <h3 className="text-base sm:text-lg font-bold text-foreground mb-3">{t("yourResponse")}</h3>
            <div className="writing-panel-3d rounded-xl border border-border bg-card/80 backdrop-blur-sm p-3 sm:p-5 w-full min-w-0">
              <textarea
                id="writing-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={10}
                disabled={submitting}
                className="w-full min-w-0 resize-y rounded-lg border border-border bg-background p-3 sm:p-4 text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary text-base leading-relaxed disabled:opacity-60"
                placeholder={t("writeResponsePlaceholder")}
              />
            </div>
            <div className="flex flex-wrap justify-between gap-3 mt-3 text-sm text-muted-foreground">
              <div className="flex flex-wrap gap-3 sm:gap-4 tabular-nums">
                <span>
                  {t("wordsLabel")}: {wordCount}
                </span>
                <span>
                  {t("sentencesLabel")}: {sentenceCount}
                </span>
              </div>
              <span className="text-xs">{minWordsHint}</span>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row gap-3 mb-6 w-full min-w-0">
            <button
              type="button"
              onClick={() => submit()}
              disabled={!text.trim() || submitting}
              className="button-glow futuristic-button flex-1 min-w-0 bg-gradient-to-r from-primary to-accent text-primary-foreground px-4 sm:px-6 py-3.5 rounded-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-[1.02] disabled:hover:scale-100 text-sm sm:text-base"
            >
              {submitting ? t("gradingWriting") : t("submitForGrade")}
            </button>
            <button
              type="button"
              onClick={backToLessons}
              disabled={loading || submitting}
              className="w-full sm:w-auto shrink-0 px-6 py-3.5 border-2 border-border bg-card/80 text-foreground rounded-xl font-bold hover:bg-muted/50 transition-all disabled:opacity-50 text-sm sm:text-base"
            >
              {t("writingBackToLessons")}
            </button>
          </div>

          {submitting && (
            <p className="text-sm text-muted-foreground text-center mb-6 animate-pulse px-2">
              {t("aiReadingEssay")}
            </p>
          )}

          {error && prompt && (
            <div className="mb-6 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive break-words">
              {error}
            </div>
          )}

          {submitted && (
            <div className="mb-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-300">
              {t("writingSaved")}
              {attempts.length > 0 && (
                <span className="text-emerald-700/80 dark:text-emerald-400/80">
                  {" "}
                  · {t("attemptLabel")} {attempts.length}
                </span>
              )}
            </div>
          )}

          {submitNotice && (
            <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200 break-words">
              {submitNotice}
            </div>
          )}

          {score !== null && (
            <div className="writing-panel-3d ui-reveal rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/5 to-accent/5 p-5 sm:p-6 md:p-8 text-center w-full min-w-0 mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-foreground mb-2">{t("yourGrade")}</h3>
              <div className="text-3xl sm:text-4xl font-bold text-primary mb-2 tabular-nums">
                {Math.round(score)}%
              </div>
              <p className="text-muted-foreground mb-2 text-sm sm:text-base">
                {score >= 80
                  ? t("excellentWriting")
                  : score >= 60
                    ? t("goodWorkKeepPracticing")
                    : t("keepWritingDraft")}
              </p>
              <p className="text-xs text-muted-foreground">
                {scoringSource === "ai" ? t("scoreFromAi") : t("scoreFromLength")}
                {" · "}
                {wordCount} {t("wordsLabel")} · {sentenceCount} {t("sentencesLabel")}
              </p>
            </div>
          )}

          <WritingFeedbackPanel feedback={aiFeedback} scoringSource={scoringSource} t={t} />
        </div>
      </div>

      {showResubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl p-5 sm:p-6 space-y-4 writing-panel-3d max-h-[90vh] overflow-y-auto">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">
                {t("writingResubmitTitle")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("readingResubmitAlready")} {resubmitInfo.attempts}{" "}
                {resubmitInfo.attempts === 1 ? t("readingResubmitTime") : t("readingResubmitTimes")}.{" "}
                {t("readingResubmitBest")}{" "}
                <span className="font-semibold text-primary">
                  {Math.round(resubmitInfo.bestScore)}%
                </span>
                .
              </p>
              <p className="text-sm text-muted-foreground mt-2">{t("readingResubmitNote")}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowResubmitModal(false)}
                className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl border border-border text-foreground hover:bg-muted font-medium"
              >
                {t("readingGoBack")}
              </button>
              <button
                type="button"
                onClick={() => submit(true)}
                className="button-glow flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary to-accent text-primary-foreground font-semibold"
              >
                {t("readingSubmitAnyway")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
